import React, { useState } from 'react';
import axiosInstance from '../../axiosConfig';
import GlassPaper from '../GlassPaper';
import { useAdminStrategies } from '../../hooks/useAdminStrategies';
import { strategyModelLabel } from '../../utils/strategyAccountModel';
import '../ClientLots/ClientLots.css';
import './StrategyBindings.css';

const StrategyBindings = () => {
    const { bindingsStrategies, loading: strategiesLoading } = useAdminStrategies();
    const [selectedStrategy, setSelectedStrategy] = useState('');
    const [strategyDesc, setStrategyDesc] = useState('');
    const [bindings, setBindings] = useState([]);
    const [editedValues, setEditedValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseColor, setResponseColor] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    const selectedMeta = bindingsStrategies.find((s) => s.strategy_name === selectedStrategy);

    const syncDraftFromBindings = (rows) => {
        const draft = {};
        rows.forEach((row) => {
            draft[row.app_name] = {
                enabled: row.enabled,
                live_orders: row.live_orders,
                equity: row.equity ?? '',
                lots_normal: row.lots_normal ?? '',
                lots_escalation: row.lots_escalation ?? '',
                account_alias: row.account_alias ?? '',
                original: {
                    enabled: row.enabled,
                    live_orders: row.live_orders,
                    equity: row.equity ?? '',
                    lots_normal: row.lots_normal ?? '',
                    lots_escalation: row.lots_escalation ?? '',
                    account_alias: row.account_alias ?? '',
                },
            };
        });
        setEditedValues(draft);
    };

    const handleStrategyChange = async (e) => {
        const selected = e.target.value;
        setSelectedStrategy(selected);
        const strategy = bindingsStrategies.find((s) => s.strategy_name === selected);
        setStrategyDesc(strategy ? strategy.strategy_desc : '');
        setResponseMessage('');
        setHasChanges(false);
        setEditedValues({});

        if (!selected) {
            setBindings([]);
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/strategy-bindings/${selected}`);
            setBindings(response.data);
            syncDraftFromBindings(response.data);
        } catch (error) {
            console.error('Error fetching strategy bindings:', error);
            setResponseMessage(
                error.response?.data?.message || 'Error loading strategy account bindings'
            );
            setResponseColor('red');
            setBindings([]);
        } finally {
            setLoading(false);
        }
    };

    const markChanged = (nextValues) => {
        const changed = Object.keys(nextValues).some((key) => {
            const current = nextValues[key];
            const orig = current.original;
            return (
                current.enabled !== orig.enabled ||
                current.live_orders !== orig.live_orders ||
                String(current.equity) !== String(orig.equity) ||
                String(current.lots_normal) !== String(orig.lots_normal) ||
                String(current.lots_escalation) !== String(orig.lots_escalation) ||
                String(current.account_alias) !== String(orig.account_alias)
            );
        });
        setHasChanges(changed);
    };

    const handleFieldChange = (appName, field, value) => {
        const next = {
            ...editedValues,
            [appName]: {
                ...editedValues[appName],
                [field]: value,
            },
        };
        setEditedValues(next);
        markChanged(next);
    };

    const handleSave = async () => {
        if (!hasChanges) {
            setResponseMessage('No changes to save');
            setResponseColor('orange');
            return;
        }

        setSaving(true);
        try {
            const updates = [];
            Object.keys(editedValues).forEach((appName) => {
                const current = editedValues[appName];
                const orig = current.original;
                const changed =
                    current.enabled !== orig.enabled ||
                    current.live_orders !== orig.live_orders ||
                    String(current.equity) !== String(orig.equity) ||
                    String(current.lots_normal) !== String(orig.lots_normal) ||
                    String(current.lots_escalation) !== String(orig.lots_escalation) ||
                    String(current.account_alias) !== String(orig.account_alias);
                if (!changed) {
                    return;
                }
                updates.push({
                    app_name: appName,
                    enabled: current.enabled,
                    live_orders: current.live_orders,
                    equity: current.equity === '' ? null : Number(current.equity),
                    lots_normal: current.lots_normal === '' ? null : parseInt(current.lots_normal, 10),
                    lots_escalation:
                        current.lots_escalation === ''
                            ? null
                            : parseInt(current.lots_escalation, 10),
                    account_alias: current.account_alias || null,
                });
            });

            await axiosInstance.post(`/api/strategy-bindings/${selectedStrategy}`, { updates });

            setResponseMessage(`Successfully updated ${updates.length} account binding(s)`);
            setResponseColor('green');

            const response = await axiosInstance.get(`/api/strategy-bindings/${selectedStrategy}`);
            setBindings(response.data);
            syncDraftFromBindings(response.data);
            setHasChanges(false);
        } catch (error) {
            console.error('Error saving strategy bindings:', error);
            setResponseMessage(error.response?.data?.message || 'Error saving account bindings');
            setResponseColor('red');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        syncDraftFromBindings(bindings);
        setHasChanges(false);
        setResponseMessage('');
    };

    return (
        <GlassPaper className="strategy-bindings-shell client-lots-shell" elevation={0} square={false}>
            <h2 className="dashboard-title">Strategy Accounts</h2>

            <div className="model-banner">
                <strong>quant_platform account model.</strong> Assign Zerodha accounts directly via{' '}
                <code>qp_strategy_account_bindings</code>. Legacy strategies (banknifty8, etc.) continue
                to use <strong>Client lots</strong> and classification tables — unchanged.
            </div>

            <div className="form-group mt-3">
                <label htmlFor="strategySelectBindings">Strategy Name</label>
                <select
                    id="strategySelectBindings"
                    className="form-control"
                    value={selectedStrategy}
                    onChange={handleStrategyChange}
                    disabled={strategiesLoading}
                >
                    <option value="">Select a quant_platform strategy</option>
                    {bindingsStrategies.map((strategy) => (
                        <option key={strategy.strategy_name} value={strategy.strategy_name}>
                            {strategy.strategy_name}
                        </option>
                    ))}
                </select>
                {strategyDesc && <p className="description-text">{strategyDesc}</p>}
                {selectedMeta && (
                    <p className="description-text">{strategyModelLabel(selectedMeta)}</p>
                )}
            </div>

            {loading && <div className="loading-message">Loading account bindings...</div>}

            {!loading && selectedStrategy && bindings.length > 0 && (
                <div className="client-lots-table-container">
                    <table className="client-lots-table">
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Enabled</th>
                                <th>Live orders</th>
                                <th>Equity</th>
                                <th>Lots normal</th>
                                <th>Lots escalation</th>
                                <th>Alias</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bindings.map((row) => (
                                <tr key={row.app_name}>
                                    <td className="client-name-cell">
                                        <div className="client-name">{row.app_name}</div>
                                        {row.account_name && (
                                            <div className="client-alias">{row.account_name}</div>
                                        )}
                                    </td>
                                    <td className="toggle-cell">
                                        <input
                                            type="checkbox"
                                            checked={editedValues[row.app_name]?.enabled ?? false}
                                            onChange={(e) =>
                                                handleFieldChange(row.app_name, 'enabled', e.target.checked)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="form-control form-control-sm live-orders-select"
                                            value={
                                                editedValues[row.app_name]?.live_orders === null
                                                    ? 'inherit'
                                                    : editedValues[row.app_name]?.live_orders
                                                      ? 'true'
                                                      : 'false'
                                            }
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                handleFieldChange(
                                                    row.app_name,
                                                    'live_orders',
                                                    v === 'inherit' ? null : v === 'true'
                                                );
                                            }}
                                        >
                                            <option value="inherit">Inherit global</option>
                                            <option value="true">Live</option>
                                            <option value="false">Paper</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={editedValues[row.app_name]?.equity ?? ''}
                                            min="0"
                                            step="1000"
                                            onChange={(e) =>
                                                handleFieldChange(row.app_name, 'equity', e.target.value)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={editedValues[row.app_name]?.lots_normal ?? ''}
                                            min="0"
                                            step="1"
                                            placeholder="inherit"
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    row.app_name,
                                                    'lots_normal',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={editedValues[row.app_name]?.lots_escalation ?? ''}
                                            min="0"
                                            step="1"
                                            placeholder="inherit"
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    row.app_name,
                                                    'lots_escalation',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={editedValues[row.app_name]?.account_alias ?? ''}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    row.app_name,
                                                    'account_alias',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && selectedStrategy && bindings.length === 0 && (
                <div className="no-data-message">
                    No account bindings for this strategy. Add rows via SQL migration or a future
                    &quot;Add account&quot; action.
                </div>
            )}

            {!bindingsStrategies.length && !strategiesLoading && (
                <div className="no-data-message">
                    No quant_platform strategies registered in qp_strategy_registry yet.
                </div>
            )}

            {selectedStrategy && bindings.length > 0 && (
                <div className="action-buttons">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                        type="button"
                    >
                        Reset
                    </button>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        type="button"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            )}

            {responseMessage && (
                <div className="response-message" style={{ color: responseColor }}>
                    {responseMessage}
                    <button
                        className="btn btn-secondary btn-sm mt-2"
                        onClick={() => setResponseMessage('')}
                        type="button"
                    >
                        Ok
                    </button>
                </div>
            )}
        </GlassPaper>
    );
};

export default StrategyBindings;
